import {createLogger} from "@gongt/ts-stl-library/log/debug";
import {LOG_LEVEL} from "@gongt/ts-stl-library/log/levels";
import {IUserProfileBase} from "../bridge/define";
import {UserPermissionConstructor} from "../bridge/define.permission";
import {DumpPermissionResponse} from "../bridge/protocol/dump-permission.protocol";
import {UserCenter} from "../includes/user-center";
import {UserCenterApi} from "../includes/user-center-api";
import {
	createClassDynamic,
	createProjectAbstractPermission,
	IPermDataArg,
	permDataObject,
} from "../includes/user-permission";
import {memoryCache} from "./cache";

const debug = createLogger(LOG_LEVEL.SILLY, 'perm');

export class PermissionSystem<IProfile extends IUserProfileBase> {
	private api: UserCenterApi<IProfile>;
	public readonly AbstractClass: UserPermissionConstructor;
	protected registry: Map<string, UserPermissionConstructor>;
	
	constructor(private uc: UserCenter<IProfile>) {
		this.registry = new Map;
		this.api = uc.api;
		this.AbstractClass = createProjectAbstractPermission(uc.projectName);
	}
	
	/** 生成一个名为 name 的权限类，并注册它的名称 */
	createClass(name: string): UserPermissionConstructor {
		if (this.registry.has(name)) {
			return this.registry.get(name);
		}
		if (!name) {
			throw new TypeError('createPermission(): require argument `name`.')
		}
		return this.registerClass(createClassDynamic(name, this.AbstractClass));
	}
	
	/** 注册一个权限类的名称 */
	registerClass(cls: UserPermissionConstructor): UserPermissionConstructor {
		if (!cls) {
			throw new TypeError('can not register permission class: undefined.');
		}
		debug('register class: ', cls.name);
		this.registry.set(cls.name, cls);
		return cls;
	}
	
	async grant(userId: string, perm: IPermDataArg): Promise<void> {
		let permData = permDataObject(perm);
		await this.api.grantPermission({
			userId,
			...permData,
		});
		await memoryCache.del(['perm-', userId, permData.permName, permData.scope]);
	}
	
	async check(userId: string, perm: IPermDataArg): Promise<boolean> {
		let permData = permDataObject(perm);
		const cached: boolean = await memoryCache.get(['perm-', userId, permData.permName, permData.scope]);
		
		if (cached !== undefined) {
			return cached;
		}
		const permName = permData.permName;
		const {available, expire, ...args} = permData;
		return this.api.checkPermission({
			userId,
			...args,
		}).then((ret) => {
			memoryCache.set(['perm-', userId, permName, permData.scope], ret.hasPermission, ret.expireSeconds)
			           .catch(memoryCache.ignoreError);
			return ret.hasPermission
		});
	}
	
	async drop(userId: string, perm: IPermDataArg): Promise<void> {
		let permData = permDataObject(perm);
		const {available, expire, ...args} = permData;
		await this.api.dropPermission({
			userId,
			...args,
			allScope: false,
		});
		await memoryCache.del(['perm-', userId, permData.permName, permData.scope]);
	}
	
	async dropLike(userId: string, perm: IPermDataArg): Promise<void> {
		let permData = permDataObject(perm);
		const {available, expire, ...args} = permData;
		await this.api.dropPermission({
			userId,
			...args,
			allScope: true,
		});
		await memoryCache.del(['perm-', userId, permData.permName, permData.scope]);
	}
	
	// no project -> current project
	dump(userId: string, project?: string): Promise<DumpPermissionResponse> {
		return this.uc.api.dumpPermission({
			userId,
			project,
		});
	}
}
